import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Target, TrendingUp, BarChart2, Calendar,
  ChevronRight, Award, Zap, Clock, RotateCcw,
  CheckCircle2, Flame, ArrowUpRight, Check,
  PhoneCall, ShieldCheck, Sparkles, AlertCircle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { targetsApi, authHelpers, fetchApiDirect } from '../services/api';

// Initial Empty Target State before API loads
const INITIAL_TARGETS = [
  {
    id: 'orders',
    label: 'Orders Target',
    shortLabel: 'Orders',
    icon: Target,
    value: 0,
    achieved: 0,
    period: 'Monthly Goal',
    color: '#3B82F6',
    bgColor: '#EFF6FF',
    assignedBy: 'Unassigned',
    dateRange: 'No Active Cycle',
    unit: 'orders',
    prefix: '',
    suffix: ' orders',
  },
  {
    id: 'revenue',
    label: 'Revenue Target',
    shortLabel: 'Revenue',
    icon: TrendingUp,
    value: 0,
    achieved: 0,
    period: 'Monthly Goal',
    color: '#10B981',
    bgColor: '#ECFDF5',
    assignedBy: 'Unassigned',
    dateRange: 'No Active Cycle',
    unit: '₹',
    prefix: '₹',
    suffix: '',
  },
  {
    id: 'conversion',
    label: 'Conversion Target',
    shortLabel: 'Conversion',
    icon: BarChart2,
    value: 0,
    achieved: 0,
    period: 'Monthly Goal',
    color: '#8B5CF6',
    bgColor: '#F5F3FF',
    assignedBy: 'Unassigned',
    dateRange: 'No Active Cycle',
    unit: '%',
    prefix: '',
    suffix: '%',
  },
];

// Formatting helper
function formatValue(type, val) {
  const num = parseFloat(val) || 0;
  if (num === 0) {
    if (type === 'revenue') return '₹0';
    if (type === 'conversion') return '0%';
    return '0';
  }
  if (type === 'revenue') {
    if (num >= 10000000) return '₹' + (num / 10000000).toFixed(2) + ' Cr';
    if (num >= 100000) return '₹' + (num / 100000).toFixed(1) + 'L';
    if (num >= 1000) return '₹' + (num / 1000).toFixed(1) + 'K';
    return '₹' + num.toLocaleString('en-IN');
  }
  if (type === 'conversion') return num + '%';
  return num.toLocaleString('en-IN');
}

const AgentTargets = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.textColor === '#FFFFFF' || currentTheme?.color === '#0f172a';
  
  const [sprintFilter, setSprintFilter] = useState('Week'); // 'Today' | 'Week' | 'Month'
  const [expandedMonths, setExpandedMonths] = useState([]);
  const [targets, setTargets] = useState(INITIAL_TARGETS);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaStats, setMetaStats] = useState({
    efficiency: '0%',
    teamRank: '—',
    streak: '—',
    activePeriod: 'Current Period',
    aht: '4m 10s',
    dialedCalls: 0,
  });

  const [sprintKpi, setSprintKpi] = useState({
    ordersAchieved: 0,
    ordersTarget: 0,
    revenueAchieved: 0,
    revenueTarget: 0,
    conversionAchieved: 0,
    conversionTarget: 0,
    aht: '4m 10s',
    dialedCalls: 0,
  });

  const isFetchingRef = useRef(false);
  const monthKpiCacheRef = useRef(null);
  const targetsRef = useRef(targets);
  targetsRef.current = targets;

  const toggleMonth = (key) => {
    setExpandedMonths(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // ── Live Target & KPI API fetch ───────
  const fetchTargetData = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = u.id || u.employeeId || authHelpers.getUserId();
      if (!userId) {
        setLoading(false);
        return;
      }

      const roleStr = (u.role || u.designation || '').toUpperCase();
      const entityType = (roleStr.includes('TL') || roleStr.includes('TEAM_LEAD') || roleStr.includes('TEAM LEAD'))
        ? 'team-lead'
        : roleStr.includes('FLOOR')
        ? 'floor'
        : 'employee';

      // 1. Fetch Target Definitions & History
      const [orders, revenue, conversion, histRes] = await Promise.allSettled([
        targetsApi.getCurrentTarget(entityType, userId, 'ORDERS'),
        targetsApi.getCurrentTarget(entityType, userId, 'REVENUE'),
        targetsApi.getCurrentTarget(entityType, userId, 'CONVERSION'),
        entityType === 'floor' ? targetsApi.getFloorHistory(userId) : targetsApi.getEmployeeHistory(userId),
      ]);

      const ordersVal = orders.status === 'fulfilled' ? orders.value : null;
      const revenueVal = revenue.status === 'fulfilled' ? revenue.value : null;
      const conversionVal = conversion.status === 'fulfilled' ? conversion.value : null;
      const histData = histRes.status === 'fulfilled' ? histRes.value : null;

      const parseItem = (d) => {
        if (!d) return {};
        return Array.isArray(d) ? (d[0] || {}) : (d.data?.target || d.data?.result || d.data || d.result || d);
      };

      const rawOrders = parseItem(ordersVal);
      const rawRevenue = parseItem(revenueVal);
      const rawConversion = parseItem(conversionVal);

      const sDate = rawOrders.startDate || rawRevenue.startDate || rawConversion.startDate || dayjs().startOf('month').format('YYYY-MM-DD');
      const eDate = rawOrders.endDate || rawRevenue.endDate || rawConversion.endDate || dayjs().endOf('month').format('YYYY-MM-DD');

      const formattedStartDate = dayjs(sDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).isValid()
        ? dayjs(sDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('YYYY-MM-DD')
        : dayjs().startOf('month').format('YYYY-MM-DD');

      const formattedEndDate = dayjs(eDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).isValid()
        ? dayjs(eDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('YYYY-MM-DD')
        : dayjs().endOf('month').format('YYYY-MM-DD');

      // 2. Fetch Live Achieved KPI data from /dashboard/business/kpi
      const [kpiOrdersRes, kpiRevenueRes, kpiConversionRes] = await Promise.allSettled([
        fetchApiDirect(`/dashboard/business/kpi?type=ORDERS&employeeId=${userId}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`),
        fetchApiDirect(`/dashboard/business/kpi?type=REVENUE&employeeId=${userId}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`),
        fetchApiDirect(`/dashboard/business/kpi?type=CONVERSION&employeeId=${userId}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`),
      ]);

      const kpiOrders = kpiOrdersRes.status === 'fulfilled' ? (kpiOrdersRes.value?.data || kpiOrdersRes.value) : null;
      const kpiRevenue = kpiRevenueRes.status === 'fulfilled' ? (kpiRevenueRes.value?.data || kpiRevenueRes.value) : null;
      const kpiConversion = kpiConversionRes.status === 'fulfilled' ? (kpiConversionRes.value?.data || kpiConversionRes.value) : null;

      // Achieved values extracted live from orders placed
      const achievedOrdersCount = Number(
        kpiOrders?.ordersPlaced ??
        kpiOrders?.orders ??
        kpiOrders?.totalOrdersPlaced ??
        kpiOrders?.totalOrders ??
        kpiOrders?.achieved ??
        kpiOrders?.achievedValue ??
        kpiOrders?.count ??
        0
      );

      const achievedRevenueVal = Number(
        kpiRevenue?.revenueGenerated ??
        kpiRevenue?.revenue ??
        kpiRevenue?.totalRevenue ??
        kpiRevenue?.ordersPlaced ??
        kpiRevenue?.achieved ??
        kpiRevenue?.achievedValue ??
        0
      );

      const achievedConversionVal = Number(
        kpiConversion?.grossConversionRate ??
        kpiConversion?.grossConversion ??
        kpiConversion?.conversionRate ??
        kpiConversion?.conversion ??
        kpiConversion?.grossConversionPct ??
        kpiConversion?.achieved ??
        0
      );

      // Build Target Item
      const parseTarget = (data, defaultItem, targetKey, liveAchieved) => {
        let item = parseItem(data);

        // Fallback to cached target if API returned empty
        if (!item || (!item.targetValue && !item.target_value && !item.value && !item.target && !item.current && !item.currentTarget)) {
          try {
            const cached = localStorage.getItem(`assigned_target_employee_${userId}_${targetKey}`) ||
                           localStorage.getItem(`assigned_target_agent_${userId}_${targetKey}`) ||
                           localStorage.getItem(`assigned_target_team_lead_${userId}_${targetKey}`) ||
                           localStorage.getItem(`assigned_target_latest_${targetKey}`);
            if (cached) item = JSON.parse(cached);
          } catch (e) {}
        }

        const val = Number(item?.targetValue ?? item?.target_value ?? item?.currentTarget ?? item?.currentValue ?? item?.value ?? item?.target ?? 0);
        const ach = liveAchieved > 0 ? liveAchieved : Number(item?.achieved ?? item?.achievedValue ?? item?.current ?? 0);
        const creator = item?.assignedByName || item?.assignedBy || item?.createdBy || (val > 0 ? 'Assigned by Manager' : 'Unassigned');
        const dRange = item?.startDate && item?.endDate ? `${item.startDate} - ${item.endDate}` : `${formattedStartDate} - ${formattedEndDate}`;

        return {
          ...defaultItem,
          value: val,
          achieved: ach,
          assignedBy: creator,
          dateRange: dRange,
        };
      };

      const updatedTargets = [
        parseTarget(ordersVal, INITIAL_TARGETS[0], 'ORDERS', achievedOrdersCount),
        parseTarget(revenueVal, INITIAL_TARGETS[1], 'REVENUE', achievedRevenueVal),
        parseTarget(conversionVal, INITIAL_TARGETS[2], 'CONVERSION', achievedConversionVal),
      ];

      setTargets(updatedTargets);

      // Cache and set initial monthly sprint KPI immediately
      const initialMonthKpi = {
        ordersAchieved: achievedOrdersCount,
        ordersTarget: updatedTargets[0].value,
        revenueAchieved: achievedRevenueVal,
        revenueTarget: updatedTargets[1].value,
        conversionAchieved: achievedConversionVal,
        conversionTarget: updatedTargets[2].value,
        aht: kpiOrders?.avgAHT || kpiOrders?.aht || kpiOrders?.averageHandlingTime || '4m 10s',
        dialedCalls: Number(kpiOrders?.dialedCalls || kpiOrders?.totalCalls || kpiOrders?.outreachCalls || 0),
      };
      monthKpiCacheRef.current = initialMonthKpi;
      setSprintKpi(initialMonthKpi);

      // Calculate active meta stats
      const totalTargetVal = updatedTargets[0].value;
      const totalAchievedVal = updatedTargets[0].achieved;
      const eff = totalTargetVal > 0 ? `${Math.min(Math.round((totalAchievedVal / totalTargetVal) * 100), 100)}%` : '0%';

      setMetaStats({
        efficiency: kpiOrders?.efficiency ? `${kpiOrders.efficiency}%` : eff,
        teamRank: kpiOrders?.teamRank || kpiOrders?.rank ? `#${kpiOrders.teamRank || kpiOrders.rank}` : '—',
        streak: kpiOrders?.streak ? `${kpiOrders.streak} Days` : '—',
        activePeriod: `${formattedStartDate} – ${formattedEndDate}`,
        aht: kpiOrders?.avgAHT || kpiOrders?.aht || kpiOrders?.averageHandlingTime || '4m 10s',
        dialedCalls: Number(kpiOrders?.dialedCalls || kpiOrders?.totalCalls || kpiOrders?.outreachCalls || 0),
      });

      // Normalize history response safely
      const parseHistory = (rawHist) => {
        if (!rawHist) return [];
        const list = Array.isArray(rawHist) ? rawHist : rawHist.history || rawHist.data || rawHist.content || [];
        if (!Array.isArray(list) || list.length === 0) return [];

        if (list[0]?.items) {
          return list.map((m, idx) => ({
            key: m.key || m.month || `hist-${idx}`,
            month: m.month || m.key || `Month ${idx + 1}`,
            status: m.status || 'Completed',
            achievedPct: Number(m.achievedPct ?? 100),
            items: Array.isArray(m.items) ? m.items : [],
          }));
        }

        const grouped = {};
        list.forEach((item, idx) => {
          const dateStr = item.startDate || item.createdAt || item.date;
          const monthKey = dateStr && dayjs(dateStr).isValid() ? dayjs(dateStr).format('MMMM YYYY') : item.month || `Cycle ${idx + 1}`;
          const key = dateStr && dayjs(dateStr).isValid() ? dayjs(dateStr).format('YYYY-MM') : `cycle-${idx}`;
          if (!grouped[key]) {
            grouped[key] = {
              key,
              month: monthKey,
              status: item.status || 'Completed',
              achievedPct: 0,
              items: [],
            };
          }
          const tVal = Number(item.targetValue || item.target || item.value || 0);
          const aVal = Number(item.achieved || item.achievedValue || 0);
          const color = item.targetType === 'ORDERS' ? '#3B82F6' : item.targetType === 'REVENUE' ? '#10B981' : '#8B5CF6';

          grouped[key].items.push({
            type: item.targetType || 'TARGET',
            target: tVal,
            achieved: aVal,
            met: aVal >= tVal && tVal > 0,
            color,
          });
        });

        return Object.values(grouped).map((g) => {
          const totalT = g.items.reduce((acc, it) => acc + it.target, 0);
          const totalA = g.items.reduce((acc, it) => acc + it.achieved, 0);
          g.achievedPct = totalT > 0 ? Math.min(Math.round((totalA / totalT) * 100), 100) : 100;
          return g;
        });
      };

      const parsedHistory = parseHistory(histData);
      setHistory(parsedHistory);
      if (parsedHistory.length > 0 && parsedHistory[0]?.key) {
        setExpandedMonths([parsedHistory[0].key]);
      }
    } catch (err) {
      console.error('Target & KPI API load error:', err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        isFetchingRef.current = false;
      }, 500);
    }
  }, []);

  useEffect(() => {
    fetchTargetData();
  }, [fetchTargetData]);

  // ── Sprint Filter (Today / Week / Month) Live KPI Fetch ──
  useEffect(() => {
    // If Sprint Filter is 'Month', reuse cached data — no extra network calls
    if (sprintFilter === 'Month') {
      if (monthKpiCacheRef.current) {
        setSprintKpi(monthKpiCacheRef.current);
      }
      return;
    }

    const fetchSprintKPI = async () => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = u.id || u.employeeId || authHelpers.getUserId();
        if (!userId) return;

        const divisor = sprintFilter === 'Today' ? 24 : 4;
        const sprintStart = sprintFilter === 'Today'
          ? dayjs().format('YYYY-MM-DD')
          : dayjs().subtract(6, 'day').format('YYYY-MM-DD');
        const sprintEnd = dayjs().format('YYYY-MM-DD');

        const [kpiOrdersRes, kpiRevenueRes, kpiConversionRes] = await Promise.allSettled([
          fetchApiDirect(`/dashboard/business/kpi?type=ORDERS&employeeId=${userId}&startDate=${sprintStart}&endDate=${sprintEnd}`),
          fetchApiDirect(`/dashboard/business/kpi?type=REVENUE&employeeId=${userId}&startDate=${sprintStart}&endDate=${sprintEnd}`),
          fetchApiDirect(`/dashboard/business/kpi?type=CONVERSION&employeeId=${userId}&startDate=${sprintStart}&endDate=${sprintEnd}`),
        ]);

        const kOrders = kpiOrdersRes.status === 'fulfilled' ? (kpiOrdersRes.value?.data || kpiOrdersRes.value) : null;
        const kRevenue = kpiRevenueRes.status === 'fulfilled' ? (kpiRevenueRes.value?.data || kpiRevenueRes.value) : null;
        const kConversion = kpiConversionRes.status === 'fulfilled' ? (kpiConversionRes.value?.data || kpiConversionRes.value) : null;

        const currentT = targetsRef.current;
        const sOrdersAchieved = Number(kOrders?.ordersPlaced ?? kOrders?.orders ?? kOrders?.totalOrdersPlaced ?? kOrders?.totalOrders ?? kOrders?.achieved ?? 0);
        const sRevenueAchieved = Number(kRevenue?.revenueGenerated ?? kRevenue?.revenue ?? kRevenue?.totalRevenue ?? 0);
        const sConversionAchieved = Number(kConversion?.grossConversionRate ?? kConversion?.grossConversion ?? kConversion?.conversionRate ?? 0);

        setSprintKpi({
          ordersAchieved: sOrdersAchieved,
          ordersTarget: Math.round((currentT[0]?.value || 0) / divisor),
          revenueAchieved: sRevenueAchieved,
          revenueTarget: Math.round((currentT[1]?.value || 0) / divisor),
          conversionAchieved: sConversionAchieved,
          conversionTarget: Math.round((currentT[2]?.value || 0) / divisor),
          aht: kOrders?.avgAHT || kOrders?.aht || '4m 10s',
          dialedCalls: Number(kOrders?.dialedCalls || kOrders?.totalCalls || 0),
        });
      } catch (e) {
        console.warn('Sprint KPI fetch error:', e);
      }
    };
    fetchSprintKPI();
  }, [sprintFilter]);

  return (
    <div className="w-full space-y-6 animate-in fade-in duration-300">

      {/* ── Top Header & Key Metric Highlights ── */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-2 border-b border-gray-100 dark:border-white/10">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight">
              Your Assigned Targets
            </h1>
            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              Live Backend Data
            </span>
          </div>
          <p className="text-xs text-gray-500 font-medium mt-1 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span>Target Period: <strong>{metaStats.activePeriod}</strong></span>
          </p>
        </div>

        {/* Highlight Pills */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl border bg-white dark:bg-[#1e293b] border-gray-200/80 dark:border-white/10 shadow-sm">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Zap className="w-4 h-4 fill-blue-500" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">Efficiency</span>
              <span className="text-base font-black text-gray-900 dark:text-white">{metaStats.efficiency}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl border bg-white dark:bg-[#1e293b] border-gray-200/80 dark:border-white/10 shadow-sm">
            <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <Award className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">Team Rank</span>
              <span className="text-base font-black text-gray-900 dark:text-white">{metaStats.teamRank}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl border bg-white dark:bg-[#1e293b] border-gray-200/80 dark:border-white/10 shadow-sm">
            <div className="w-9 h-9 rounded-xl bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center">
              <Flame className="w-4 h-4 fill-orange-500" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">Streak</span>
              <span className="text-base font-black text-gray-900 dark:text-white">{metaStats.streak}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3 Primary KPI Target Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {targets.map((t) => {
          const pct = t.value > 0 ? Math.min(Math.round((t.achieved / t.value) * 100), 100) : 0;
          const remaining = Math.max(0, t.value - t.achieved);
          const isCompleted = t.value > 0 && t.achieved >= t.value;

          return (
            <div
              key={t.id}
              className="p-6 rounded-3xl border bg-white dark:bg-[#1e293b] border-gray-200/90 dark:border-white/10 shadow-sm hover:shadow-md transition-all flex flex-col justify-between relative overflow-hidden group"
            >
              {/* Top Row */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">
                      {t.period}
                    </span>
                    <h3 className="text-base font-black text-gray-900 dark:text-white">{t.label}</h3>
                  </div>

                  <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: `${t.color}15`, color: t.color }}
                  >
                    <t.icon className="w-5 h-5" />
                  </div>
                </div>

                {/* Progress Numbers */}
                <div className="my-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black text-gray-900 dark:text-white">
                      {formatValue(t.id, t.achieved)}
                    </span>
                    <span className="text-sm font-bold text-gray-400">
                      / {formatValue(t.id, t.value)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-xs font-bold mt-2">
                    <span className="text-gray-500">Progress</span>
                    <span style={{ color: t.color }}>{pct}% Achieved</span>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-3 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden p-0.5 mt-1.5">
                    <div
                      className="h-full rounded-full transition-all duration-700 shadow-sm"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: t.color,
                      }}
                    />
                  </div>
                </div>

                {/* Mini Metric Tag */}
                <div className="flex items-center gap-2 mt-4 text-xs font-semibold">
                  <span className="px-2.5 py-1 rounded-xl bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300">
                    Remaining: <strong className="text-gray-900 dark:text-white">{formatValue(t.id, remaining)}</strong>
                  </span>
                  {t.value === 0 ? (
                    <span className="px-2.5 py-1 rounded-xl bg-gray-100 text-gray-500 font-bold">
                      Not Assigned
                    </span>
                  ) : isCompleted ? (
                    <span className="px-2.5 py-1 rounded-xl bg-emerald-50 text-emerald-600 font-bold flex items-center gap-1">
                      <Check className="w-3.5 h-3.5 stroke-[3]" /> Target Met
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-xl bg-blue-50 text-blue-600 font-bold">
                      In Progress
                    </span>
                  )}
                </div>
              </div>

              {/* Card Footer Details */}
              <div className="mt-5 pt-3.5 border-t border-gray-100 dark:border-white/10 flex items-center justify-between text-[11px] text-gray-400 font-medium">
                <span>Assigned By: <strong className="text-gray-700 dark:text-gray-200">{t.assignedBy}</strong></span>
                <span>{t.dateRange.includes(' - ') ? t.dateRange.split(' - ')[1] : t.dateRange}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Main 2-Column Section: Performance & History ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left Column (8 Cols): Performance Breakdown */}
        <div className="lg:col-span-8 p-6 md:p-7 rounded-3xl border bg-white dark:bg-[#1e293b] border-gray-200/90 dark:border-white/10 shadow-sm space-y-6">
          
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-500">
                <Zap className="w-5 h-5 fill-amber-500" />
              </div>
              <div>
                <h3 className="text-base font-black text-gray-900 dark:text-white">
                  {sprintFilter === 'Today' ? 'Today’s Performance' : sprintFilter === 'Week' ? 'Weekly Performance & Pace' : 'Monthly Performance Overview'}
                </h3>
                <p className="text-xs text-gray-400">Live operational progress and pace</p>
              </div>
            </div>

            {/* Timeframe Filter Pills */}
            <div className="flex items-center p-1 rounded-xl bg-slate-100 dark:bg-black/30 border border-slate-200 dark:border-white/10 text-xs font-bold">
              {['Today', 'Week', 'Month'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSprintFilter(tab)}
                  className={`px-3.5 py-1.5 rounded-lg transition-all ${
                    sprintFilter === tab
                      ? 'bg-white dark:bg-[#1e293b] text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* 2 Sub-panels: Live Progress Meters & Priority KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            
            {/* Live Progress Meters */}
            <div className="space-y-3">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">
                {sprintFilter} Progress Breakdown
              </span>

              {targets.map((t) => {
                const periodTarget = t.id === 'orders' ? sprintKpi.ordersTarget : t.id === 'revenue' ? sprintKpi.revenueTarget : sprintKpi.conversionTarget;
                const periodAchieved = t.id === 'orders' ? sprintKpi.ordersAchieved : t.id === 'revenue' ? sprintKpi.revenueAchieved : sprintKpi.conversionAchieved;
                const pPct = periodTarget > 0 ? Math.min(Math.round((periodAchieved / periodTarget) * 100), 100) : 0;

                return (
                  <div
                    key={t.id}
                    className="p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-slate-50/70 dark:bg-white/5 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <t.icon className="w-4 h-4" style={{ color: t.color }} />
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">{t.shortLabel} Target</span>
                      </div>
                      <span className="text-xs font-black" style={{ color: t.color }}>{pPct}%</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatValue(t.id, periodAchieved)} done</span>
                      <span>Target: {formatValue(t.id, periodTarget)}</span>
                    </div>

                    <div className="h-2 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pPct}%`, backgroundColor: t.color }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Priority Key Operational Metrics (Dynamic based on Live Target Data) */}
            <div className="space-y-3">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">
                Operational KPIs & Velocity
              </span>

              {[
                {
                  label: sprintFilter === 'Today' ? 'Daily Orders Velocity' : sprintFilter === 'Week' ? 'Weekly Orders Velocity' : 'Monthly Velocity',
                  value: `${formatValue('orders', sprintKpi.ordersAchieved)} Orders`,
                  targetText: `Target: ${formatValue('orders', sprintKpi.ordersTarget)}`,
                  icon: TrendingUp,
                  color: '#3B82F6',
                },
                {
                  label: 'Average Handling Time (AHT)',
                  value: sprintKpi.aht || '4m 10s',
                  targetText: 'Benchmark: < 4m 30s (Optimal)',
                  icon: Clock,
                  color: '#10B981',
                },
                {
                  label: 'Dialed Calls / Outreach',
                  value: `${sprintKpi.dialedCalls || 0} Calls`,
                  targetText: 'Connected: Live Dialer',
                  icon: PhoneCall,
                  color: '#8B5CF6',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-4 rounded-2xl border border-gray-100 dark:border-white/5 bg-slate-50/70 dark:bg-white/5 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm"
                      style={{ backgroundColor: item.color }}
                    >
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200">{item.label}</h4>
                      <p className="text-[11px] text-gray-400 mt-0.5">{item.targetText}</p>
                    </div>
                  </div>

                  <span className="text-sm font-black text-gray-900 dark:text-white flex-shrink-0">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

          </div>

        </div>

        {/* Right Column (4 Cols): Targets History Accordion */}
        <div className="lg:col-span-4 p-6 md:p-7 rounded-3xl border bg-white dark:bg-[#1e293b] border-gray-200/90 dark:border-white/10 shadow-sm space-y-4 flex flex-col justify-between">
          
          <div>
            <div className="flex items-center gap-2.5 pb-2 border-b border-gray-100 dark:border-white/10 mb-4">
              <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-white/5 text-gray-700 dark:text-gray-200 flex items-center justify-center">
                <RotateCcw className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-base font-black text-gray-900 dark:text-white">Targets History</h3>
                <p className="text-[11px] text-gray-400">Past monthly performance logs</p>
              </div>
            </div>

            {/* Month Items Accordion */}
            <div className="space-y-3">
              {history.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-xs font-medium border border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-4">
                  No past target history records found in database.
                </div>
              ) : (
                history.map((m) => {
                const isExp = expandedMonths.includes(m.key);

                return (
                  <div
                    key={m.key}
                    className="border rounded-2xl border-gray-200/80 dark:border-white/10 overflow-hidden bg-white dark:bg-black/20 shadow-sm"
                  >
                    <div
                      onClick={() => toggleMonth(m.key)}
                      className="p-3.5 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 transition-colors select-none"
                    >
                      <div className="flex items-center gap-2.5">
                        <Calendar className="w-4 h-4 text-emerald-600" />
                        <span className="text-xs font-bold text-gray-900 dark:text-white">{m.month}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          m.achievedPct >= 100
                            ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300'
                            : 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-300'
                        }`}>
                          {m.achievedPct}%
                        </span>
                        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExp ? 'rotate-90' : ''}`} />
                      </div>
                    </div>

                    {isExp && (
                      <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-white/5 space-y-2.5 bg-slate-50/50 dark:bg-white/[0.02]">
                        {(m.items || []).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                              <span className="font-semibold text-gray-700 dark:text-gray-300">{item.type}</span>
                            </div>
                            <div className="flex items-center gap-2 font-bold">
                              <span className="text-gray-900 dark:text-white">
                                {item.achieved} <span className="text-gray-400 font-normal">/ {item.target}</span>
                              </span>
                              {item.met ? (
                                <span className="text-[10px] text-emerald-600 font-bold">✓ Met</span>
                              ) : (
                                <span className="text-[10px] text-red-500 font-bold">❌</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
            </div>
          </div>

          <div className="pt-2 text-center">
            <span className="text-[11px] text-gray-400 font-medium">
              Data synchronized with central sales server
            </span>
          </div>

        </div>

      </div>

    </div>
  );
};

export default AgentTargets;